import { useCallback, useContext, useEffect, useState } from 'react';

import type { GridSortModel } from '@mui/x-data-grid';
import { useHistory } from 'react-router-dom';

import { Site, TimeAndAttendanceLocation } from '@vyce/core/src/types';

import { deleteSiteRequest, getSitesRequest } from '@vyce/core/src/api/time';
import { formatSortModel } from '@vyce/core/src/utils/sorting';
import { siteToLocation } from '@vyce/core/src/modules/timeModule/utils';
import { GRID_PAGE_SIZE, TABLE_OFFSET_DELAY } from '@vyce/core/src/constants';
import { DeviceContext } from '@vyce/core/src/contexts';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { useDebounceValue } from '@vyce/core/src/hooks/useDebounceValue';
import { useTable } from '@vyce/core/src/hooks/useTable';
import { isNil } from '@vyce/core/src/utils';
import { buildNameIdUrlSegment } from '@vyce/core/src/utils/url';

import { useCheckClockedInWorkers } from '../../LocationPage/hooks/useCheckClockedInWorkers';
import { Props } from '../types';
import useChatRequests from '../../../../hooks/useChatRequests';

const defaultSortModel: GridSortModel = [
  {
    field: 'created_at',
    sort: 'desc',
  },
];

export const useData = ({ selectedCompany, fetchCompanyData, user }: Props) => {
  const { handleServerError, showNotification } = useContext(NotificationContext);
  const [loading, setLoading] = useState<boolean>(false);

  const [isClosed, setIsClosed] = useState<boolean>(false);
  const [locations, setLocations] = useState<TimeAndAttendanceLocation[]>([]);
  const history = useHistory();
  const [openCloseDialog, setOpenCloseDialog] = useState<boolean>(false);
  const [selectedLocation, setSelectedLocation] = useState<TimeAndAttendanceLocation | null>(null);
  const { isMobile } = useContext(DeviceContext);
  const [closeGridAction, setCloseGridAction] = useState<boolean>(false);
  const {
    sortModel,
    offset,
    substring,
    total,
    setTotal,
    setOffset,
    handleSortModelChange,
    handlePageChange,
    handleSearchChange,
  } = useTable({ defaultSortModel });
  const creator = {
    uid: user?.uuid || '',
    name: `${user?.first_name} ${user?.last_name}`,
    email: user?.email,
    phone: user?.phone,
    avatarUrl: user?.photo?.url,
  };
  const { removeChat, createLocationChat } = useChatRequests(creator);

  const { closeForbidDialog, isForbidDIalogOpen, handleCloseClick } = useCheckClockedInWorkers({
    companyId: selectedCompany?.uuid,
    successCallback: () => setOpenCloseDialog(true),
  });

  const dOffset = useDebounceValue(offset, TABLE_OFFSET_DELAY);

  const resetCloseStatus = () => {
    setCloseGridAction(true);
    setTimeout(() => setCloseGridAction(false), 100);
  };

  const handleCloseLocationClick = async (location: TimeAndAttendanceLocation) => {
    setSelectedLocation(location);
    if (!location.uuid) {
      return;
    }
    resetCloseStatus();
    handleCloseClick(location?.uuid);
  };

  const goToLocation = (location: TimeAndAttendanceLocation, mode: string) => {
    history.push(
      `/time/locations-and-shifts/${buildNameIdUrlSegment(location.name, location.uuid ?? '')}?mode=${mode}`
    );
  };

  const getLocations = useCallback(async () => {
    if (!selectedCompany?.uuid || isNil(dOffset)) {
      return;
    }
    setLoading(true);

    try {
      const res = await getSitesRequest(selectedCompany.uuid, {
        order_by: formatSortModel<TimeAndAttendanceLocation>(sortModel),
        offset: dOffset as number,
        substring,
        limit: GRID_PAGE_SIZE,
        show_deleted: isClosed,
        only_inactive: !isClosed,
        only_active: isClosed,
      });
      const locations: TimeAndAttendanceLocation[] = res.data.items.map((item: Site) =>
        siteToLocation(item)
      );
      setTotal(res.data?.count || 0);
      setLocations(locations);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      handleServerError(e);
    }
  }, [dOffset, sortModel, selectedCompany, substring, isClosed]);

  const closeLocation = async () => {
    if (!selectedCompany?.uuid || !selectedLocation?.uuid) {
      return;
    }

    try {
      await deleteSiteRequest(selectedCompany.uuid, selectedLocation.uuid);
      setSelectedLocation(null);
      setOpenCloseDialog(false);
      showNotification({ message: 'The location has been closed.', options: { variant: 'success' } });

      await getLocations();
      if (fetchCompanyData) {
        fetchCompanyData(selectedCompany.uuid);
      }
      removeChat({
        companyId: selectedCompany.uuid,
        entityId: selectedLocation.uuid,
        userId: user?.uuid || '',
      });
    } catch (e) {
      handleServerError(e);
    }
  };

  const goToCreateLocationPage = () => {
    history.push('/time/add-new-location');
  };

  const handleActiveStatusChange = (status: boolean) => {
    setIsClosed(status);
    setOffset(0);
  };

  const goToLocationChat = async (location: TimeAndAttendanceLocation) => {
    if (!selectedCompany?.uuid || !location.uuid) {
      return;
    }
    try {
      const chat = await createLocationChat({
        companyId: selectedCompany.uuid,
        locationId: location.uuid,
        locationName: location.name,
        creator,
      });
      if (chat) {
        history.push(`/messages?chatId=${chat.chatId}&companyId=${selectedCompany.uuid}`);
      }
    } catch (error) {
      handleServerError(error);
    }
  };

  useEffect(() => {
    getLocations();
  }, [getLocations]);

  return {
    isClosed,
    loading,
    closeGridAction,
    isMobile,
    handleSearchChange,
    locations,
    total,
    sortModel,
    openCloseDialog,
    isForbidDIalogOpen,
    closeForbidDialog,
    closeLocation,
    setOpenCloseDialog,
    setSelectedLocation,
    setOffset,
    handleSortModelChange,
    handlePageChange,
    handleActiveStatusChange,
    goToCreateLocationPage,
    goToLocation,
    handleCloseLocationClick,
    goToLocationChat,
  };
};
